// Dashboard Page - Main user landing page after authentication
// Shows user profile, badges, subscription status, and navigation
// Styled with comprehensive Tailwind CSS design system

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, useSignOut } from '../hooks/useAuth';
import { useEntitlements } from '../hooks/useEntitlements';
import { UserAvatar, LoadingSpinner } from '../components/ui';
import { UserBadgeList } from '../components/badges';
import { useUserBadgeDisplay } from '../hooks/useUserBadges';
import {
  useSubscription,
  usePurchase,
  useProducts,
  useBillingPortal,
  useBillingStatus,
} from '../services/subscription';
import {
  CHECKOUT_START_FAILED_MESSAGE,
  checkoutOutcomeFor,
  heapsterLiveCheckoutState,
  type CheckoutOutcome,
} from '../services/subscription/checkoutState';
import {
  ALREADY_HAVE_ACCESS_MESSAGE,
  CHECKOUT_CONFLICT_MESSAGES,
  MANAGE_SUBSCRIPTION_LABEL,
  SUBSCRIPTION_LOAD_ERROR_MESSAGE,
  purchaseConflictReason,
} from '../lib/checkoutConflict';

import { subscriptionStatusLabel } from '../services/subscription/statusLabel';

export function DashboardPage() {
  const { user, isEmailVerified, profileCompleted } = useAuth();
  const { hasEntitlement } = useEntitlements();
  const signOutMutation = useSignOut();
  const badgeData = useUserBadgeDisplay();
  const {
    subscription,
    isLoading: subscriptionLoading,
    error: subscriptionError,
  } = useSubscription();
  const { purchase, isProcessing: purchaseProcessing } = usePurchase();
  const { products, isLoading: productsLoading } = useProducts();
  const billing = useBillingStatus();
  const billingStatus =
    billing.isLoading || billing.isError ? undefined : billing.data;
  const statusLabel = subscriptionStatusLabel(billingStatus);
  const showBillingDate =
    !!billingStatus?.has_subscription &&
    !!billingStatus.subscription_status &&
    billingStatus.subscription_status !== 'trialing';
  const { openPortal, isOpening: portalOpening } = useBillingPortal();

  // What the last checkout attempt came to, kept with the uid that made it and
  // shown only to that user.
  const [lastCheckout, setLastCheckout] = useState<CheckoutOutcome | null>(
    null
  );
  const checkoutOutcome = checkoutOutcomeFor(lastCheckout, user?.uid);
  // A checkout that failed, shown in the subscription card.
  const upgradeError = checkoutOutcome?.error ?? '';
  // Set when checkout was refused because a HeapsterLive subscription already
  // exists (the backend's 409). The card then offers the billing portal.
  const checkoutConflict = checkoutOutcome?.conflict ?? null;

  // What the subscription card offers (#2218). Subscribe is enabled only when
  // the state has loaded and says "not subscribed".
  const checkoutState = heapsterLiveCheckoutState({
    signedIn: !!user,
    isLoading: subscriptionLoading,
    error: subscriptionError,
    subscription,
  });

  // An active STRIPE-billed subscription. `sources` is provider-agnostic (a
  // supapass grant also yields 'subscription'), so this is gated on the
  // Stripe-specific flag — this status card, the renewal info, and the "Manage
  // subscription" button to the billing portal are all Stripe billing concerns
  // (supapass/manual grants are surfaced elsewhere).
  const hasActiveSubscription = checkoutState === 'subscribed';

  // Get the active Stripe-billed entitlement for renewal info (Stripe-specific).
  const activeEntitlement = subscription?.entitlements
    ? Object.values(subscription.entitlements).find(
        e => e.isActive && e.hasStripeSubscription
      )
    : undefined;
  // Stripe's own period end. `periodEndDate` is merged across every source of
  // access and is not a billing date (#2209).
  const renewalDate = activeEntitlement?.stripePeriodEndDate;
  const willRenew = activeEntitlement?.willRenew ?? false;

  const handleSignOut = async () => {
    try {
      await signOutMutation.mutateAsync();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleUpgrade = async () => {
    // Checkout starts from one state only: signed in, verified, known not to
    // be subscribed, with the products loaded. The button is disabled in
    // every other, and this holds whatever calls it.
    if (
      !user ||
      !isEmailVerified ||
      productsLoading ||
      checkoutState !== 'not-subscribed'
    ) {
      return;
    }

    const { uid } = user;
    setLastCheckout(null);
    try {
      // Get monthly price ID from products
      const heapsterLiveProduct = products[0];
      const userCurrency = 'usd'; // Default to USD
      const monthlyPrice = heapsterLiveProduct?.prices.find(
        (p: { currency: string; interval: string }) =>
          p.currency === userCurrency && p.interval === 'month'
      );

      if (monthlyPrice) {
        await purchase(monthlyPrice.priceId);
      } else {
        console.error('Monthly price not found');
        setLastCheckout({ uid, error: CHECKOUT_START_FAILED_MESSAGE });
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      const conflict = purchaseConflictReason(error);
      if (conflict) {
        setLastCheckout({ uid, conflict });
        return;
      }
      setLastCheckout({
        uid,
        error:
          error instanceof Error
            ? error.message
            : CHECKOUT_START_FAILED_MESSAGE,
      });
    }
  };

  // The subscription card's footer (#2218).
  const renderSubscriptionAction = () => {
    // A subscriber, or someone the backend has just refused as one: the
    // billing portal, where both an existing subscription and a payment
    // method that needs updating are dealt with.
    if (checkoutConflict || checkoutState === 'subscribed') {
      return (
        <button
          onClick={() => void openPortal()}
          className="btn-secondary"
          disabled={portalOpening}
        >
          {MANAGE_SUBSCRIPTION_LABEL}
        </button>
      );
    }

    // Access that Stripe does not bill (Supapass, a manual grant): nothing
    // to subscribe to and nothing in the portal to manage.
    if (checkoutState === 'access-without-subscription') {
      return (
        <p className="text-sm text-gray-700 dark:text-gray-300">
          {ALREADY_HAVE_ACCESS_MESSAGE}
        </p>
      );
    }

    return (
      <div className="space-y-3">
        <button
          onClick={() => void handleUpgrade()}
          className="btn-primary"
          disabled={
            !isEmailVerified ||
            purchaseProcessing ||
            productsLoading ||
            checkoutState !== 'not-subscribed'
          }
          title={!isEmailVerified ? 'Verify email to subscribe' : ''}
        >
          {purchaseProcessing ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" />
              Processing...
            </>
          ) : (
            'Subscribe'
          )}
        </button>
        {upgradeError && (
          <p className="text-sm text-red-600 dark:text-red-400">
            {upgradeError}
          </p>
        )}
      </div>
    );
  };

  if (!user) {
    return <LoadingSpinner fullScreen message="Loading your dashboard..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                Welcome back, {user.display_name}!
              </h1>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <Link
                to="/profile"
                className="btn-secondary text-sm sm:text-base px-3 sm:px-4"
              >
                <span className="hidden sm:inline">Edit Profile</span>
                <span className="sm:hidden">Edit</span>
              </Link>
              <button
                onClick={() => void handleSignOut()}
                className="btn-secondary text-sm sm:text-base px-3 sm:px-4"
                disabled={signOutMutation.isPending}
              >
                {signOutMutation.isPending ? (
                  <span className="hidden sm:inline">Signing Out...</span>
                ) : (
                  <span className="hidden sm:inline">Sign Out</span>
                )}
                {signOutMutation.isPending ? (
                  <span className="sm:hidden">...</span>
                ) : (
                  <span className="sm:hidden">Exit</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Alert Banners */}
        <div className="space-y-4 mb-8">
          {/* Profile Completion Banner */}
          {!profileCompleted && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-blue-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    Complete Your Profile
                  </h3>

                  <div className="mt-3">
                    <Link
                      to="/complete-profile"
                      className="btn-sm bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Complete Profile
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* User Profile Card */}
          <div className="card lg:col-span-2">
            <div className="card-header">
              <h2 className="card-title">Your Profile</h2>
            </div>
            <div className="card-content">
              <div className="flex items-start space-x-4">
                {/* Profile Avatar */}
                <div className="flex-shrink-0">
                  <UserAvatar
                    displayName={user.display_name}
                    photoUrls={user.photo_url}
                    size="lg"
                  />
                </div>

                {/* Profile Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                    {user.display_name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                    {user.email}
                  </p>

                  {/* Profile Badges */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {hasEntitlement('sys:admin') && (
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300"
                        title="Full system administration access"
                      >
                        Admin
                      </span>
                    )}
                    {/*
                      The "Star" chip was removed by #1959. It rendered from
                      `hasEntitlement('sys:star')`, and that entitlement was
                      retired by #1990 and revoked from every holder, so it had
                      stopped rendering for anyone. Star is a real badge now
                      (#1972) and appears in the "Your Badges" card below.

                      The Admin chip above stays: `sys:admin` is live, and it
                      renders from the entitlement rather than from a badge.
                    */}
                    {isEmailVerified && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300">
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Verified
                      </span>
                    )}
                    {profileCompleted && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Complete
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="card-footer">
              <Link to="/profile" className="btn-primary">
                Manage Profile
              </Link>
            </div>
          </div>

          {/* Badge Display Card */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Your Badges</h2>
            </div>
            <div className="card-content">
              <UserBadgeList
                userBadges={badgeData.userBadges}
                badgeDefinitions={badgeData.badgeDefinitions}
                isLoading={badgeData.isLoading}
                error={badgeData.error}
              />
            </div>
          </div>

          {/* Subscription Card */}
          <div className="card lg:col-span-2">
            <div className="card-header">
              <h2 className="card-title">Subscription</h2>
            </div>
            <div className="card-content">
              {checkoutState === 'loading' ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner message="Loading subscription..." />
                </div>
              ) : checkoutState === 'error' ? (
                <div className="text-center py-4">
                  <p className="text-red-600 dark:text-red-400 text-sm">
                    {SUBSCRIPTION_LOAD_ERROR_MESSAGE}
                  </p>
                </div>
              ) : checkoutConflict ? (
                // In place of "No Active Subscription", which the refusal
                // has just contradicted.
                <div className="text-center py-4">
                  <p className="text-gray-700 dark:text-gray-300">
                    {CHECKOUT_CONFLICT_MESSAGES[checkoutConflict]}
                  </p>
                </div>
              ) : hasActiveSubscription ? (
                <div className="space-y-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {activeEntitlement?.name || 'HeapsterLive'}
                    </h3>
                  </div>
                  {statusLabel && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {statusLabel}
                    </p>
                  )}
                  {showBillingDate && renewalDate && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {willRenew ? 'Renews on' : 'Expires on'}{' '}
                      {new Date(renewalDate).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  )}
                </div>
              ) : checkoutState === 'access-without-subscription' ? null : (
                <div className="text-center py-4">
                  <p className="text-gray-500 dark:text-gray-400">
                    No Active Subscription
                  </p>
                </div>
              )}
            </div>
            <div className="card-footer">{renderSubscriptionAction()}</div>
          </div>

          {/* Quick Actions Card */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Quick Actions</h2>
            </div>
            <div className="card-content">
              <div className="grid grid-cols-1 gap-3">
                {/* Edit Profile */}
                <Link
                  to="/profile"
                  className="flex items-center p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
                >
                  <div className="flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-900 dark:text-white group-hover:text-gray-700 dark:group-hover:text-gray-200">
                    Edit Profile
                  </span>
                </Link>

                {/* Verify Email */}
                {!isEmailVerified && (
                  <Link
                    to="/verify-email"
                    className="flex items-center p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors group"
                  >
                    <div className="flex-shrink-0">
                      <svg
                        className="w-5 h-5 text-amber-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                    </div>
                    <span className="ml-3 text-sm font-medium text-amber-800 dark:text-amber-200">
                      Verify Email
                    </span>
                  </Link>
                )}

                {/* Complete Profile */}
                {!profileCompleted && (
                  <Link
                    to="/complete-profile"
                    className="flex items-center p-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors group"
                  >
                    <div className="flex-shrink-0">
                      <svg
                        className="w-5 h-5 text-blue-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <span className="ml-3 text-sm font-medium text-blue-800 dark:text-blue-200">
                      Complete Profile
                    </span>
                  </Link>
                )}

                {/* Account Settings */}
                <Link
                  to="/settings"
                  className="flex items-center p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
                >
                  <div className="flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-900 dark:text-white group-hover:text-gray-700 dark:group-hover:text-gray-200">
                    Account Settings
                  </span>
                </Link>

                {/* Redeem Code */}
                <Link
                  to="/redeem"
                  className="flex items-center p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
                >
                  <div className="flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5 5a3 3 0 015-2.236A3 3 0 0114.83 6H16a2 2 0 110 4h-5V9a1 1 0 10-2 0v1H4a2 2 0 110-4h1.17C5.06 5.687 5 5.35 5 5zm4 1V5a1 1 0 10-1 1h1zm3 0a1 1 0 10-1-1v1h1z"
                        clipRule="evenodd"
                      />
                      <path d="M9 11H3v5a2 2 0 002 2h4v-7zM11 18h4a2 2 0 002-2v-5h-6v7z" />
                    </svg>
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-900 dark:text-white group-hover:text-gray-700 dark:group-hover:text-gray-200">
                    Redeem Code
                  </span>
                </Link>

                {/* Admin Console - Administrative Access Only */}
                {hasEntitlement('sys:admin') && (
                  <Link
                    to="/admin"
                    className="flex items-center p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors group"
                    title="Administrative access for system management"
                  >
                    <div className="flex-shrink-0">
                      <svg
                        className="w-5 h-5 text-red-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <span className="ml-3 text-sm font-medium text-red-800 dark:text-red-200">
                      Admin Console
                    </span>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
